import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Bitcoin, Droplets, Clock, Zap, Gift, Timer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FaucetPage = () => {
  const { user, refreshUser } = useAuth();
  const [faucetStatus, setFaucetStatus] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [lastClaim, setLastClaim] = useState(null);

  const fetchFaucetStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/faucet/status`, { withCredentials: true });
      setFaucetStatus(response.data);
      setTimeRemaining(response.data.time_remaining_seconds);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch faucet status:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchFaucetStatus();
  }, [fetchFaucetStatus]);

  useEffect(() => {
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          fetchFaucetStatus();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, fetchFaucetStatus]);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const response = await axios.post(`${API}/faucet/claim`, {}, { withCredentials: true });

      if (response.data.success) {
        setLastClaim(response.data);
        toast.success(`🎉 Claimed ${response.data.amount_satoshis} satoshis!`);
        await refreshUser();
        await fetchFaucetStatus();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to claim. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const canClaim = faucetStatus?.can_claim;

  return (
    <div className="min-h-screen pt-20 pb-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 mb-6">
            <Droplets className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
            Bitcoin <span className="text-gradient-gold">Faucet</span>
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto">
            Claim free satoshis every 15 minutes. No limits on daily claims!
          </p>
        </div>

        {/* Main Faucet Card */}
        <div className="card-dark p-8 sm:p-12 text-center mb-8">
          {/* Timer Display */}
          <div className="mb-8">
            <div
              className={`inline-flex items-center justify-center w-32 h-32 rounded-full border-4 ${
                canClaim
                  ? 'border-emerald-400 bg-emerald-400/10'
                  : 'border-slate-600 bg-slate-600/10'
              } transition-all duration-500`}
            >
              {canClaim ? (
                <Gift className="w-16 h-16 text-emerald-400 animate-pulse" />
              ) : (
                <span
                  className="font-mono text-3xl font-bold text-slate-300"
                  data-testid="faucet-timer-display"
                >
                  {formatTime(timeRemaining)}
                </span>
              )}
            </div>
          </div>

          {/* Status Text */}
          <div className="mb-8">
            {canClaim ? (
              <div data-testid="faucet-claimed-status">
                <h2 className="font-heading text-2xl font-bold text-emerald-400 mb-2">
                  Ready to Claim!
                </h2>
                <p className="text-slate-400">
                  Click the button below to receive {faucetStatus?.min_reward}-
                  {faucetStatus?.max_reward} satoshis
                </p>
              </div>
            ) : (
              <div>
                <h2 className="font-heading text-2xl font-bold text-slate-300 mb-2">
                  Please Wait
                </h2>
                <p className="text-slate-400">
                  Next claim available in {formatTime(timeRemaining)}
                </p>
              </div>
            )}
          </div>

          {/* Claim Button */}
          <Button
            onClick={handleClaim}
            disabled={!canClaim || claiming}
            className={`px-12 py-6 text-lg rounded-xl transition-all ${
              canClaim
                ? 'btn-gold pulse-gold'
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
            }`}
            data-testid="faucet-claim-btn"
          >
            {claiming ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Claiming...
              </div>
            ) : canClaim ? (
              <>
                <Zap className="w-5 h-5 mr-2" />
                Claim Satoshis
              </>
            ) : (
              <>
                <Timer className="w-5 h-5 mr-2" />
                Wait {formatTime(timeRemaining)}
              </>
            )}
          </Button>

          {/* Last Claim Info */}
          {lastClaim && (
            <div className="mt-8 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 inline-block">
              <p className="text-emerald-400 flex items-center gap-2">
                <Bitcoin className="w-5 h-5" />
                <span className="font-mono font-bold">+{lastClaim.amount_satoshis} satoshis</span>
                claimed successfully!
              </p>
            </div>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="card-dark p-6 text-center">
            <Clock className="w-8 h-8 text-[#F7931A] mx-auto mb-3" />
            <h3 className="font-semibold mb-1">15 Min Cooldown</h3>
            <p className="text-sm text-slate-400">Claim every 15 minutes</p>
          </div>

          <div className="card-dark p-6 text-center">
            <Gift className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
            <h3 className="font-semibold mb-1">{faucetStatus?.min_reward}-{faucetStatus?.max_reward} Sats</h3>
            <p className="text-sm text-slate-400">Random reward per claim</p>
          </div>

          <div className="card-dark p-6 text-center">
            <Zap className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
            <h3 className="font-semibold mb-1">Unlimited Claims</h3>
            <p className="text-sm text-slate-400">No daily limits</p>
          </div>
        </div>

        {/* Current Balance */}
        <div className="mt-8 card-dark p-6 flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400 mb-1">Your Current Balance</p>
            <p className="font-mono text-2xl font-bold text-[#F7931A]">
              {user?.balance_satoshis?.toLocaleString() || 0} satoshis
            </p>
          </div>
          <Bitcoin className="w-12 h-12 text-[#F7931A]/30" />
        </div>
      </div>
    </div>
  );
};

export default FaucetPage;
